// TIP-609 — the states of the Counterparty Briefs page.
//
// Eight product states, taken from the flow guideline's "State table: the
// allowance card" (page 9). The card is ONE component throughout; what changes
// is how many counters are drawn, the colour of the top border, the note under
// each counter, which banner sits below, and which two buttons are on the right.
//
// Two further states — unclaimed and pending — are a FORK ABOVE the product:
// the allowance card and the briefs table do not render at all. They come from
// Arshad's scope note, not from the flow guideline, whose Stage 0 predates the
// claim-gate screens.
import { ENTITLEMENTS, RESET_DATES } from "./briefsFixtures";

export const GATE_STATES = ["unclaimed", "pending"];

const claimed = (weekRemaining, remaining, bought = 0) => ({
  tier: "claimed",
  week: { remaining: weekRemaining, cap: ENTITLEMENTS.claimed.weeklyCap },
  month: { remaining, allowance: ENTITLEMENTS.claimed.monthlyAllowance },
  bought,
});

// A Member has no weekly limit at all, so `week` is null. Null means NOT DRAWN:
// the counter must be absent from the DOM, not zero and not greyed out.
const member = (remaining, bought = 0) => ({
  tier: "member",
  week: null,
  month: { remaining, allowance: ENTITLEMENTS.member.monthlyAllowance },
  bought,
});

export const PRODUCT_STATES = {
  // Claimed, week and month both above zero.
  "briefs-available": claimed(2, 6),
  // Week reads 1. Identical to the above — there is deliberately no warning
  // styling at one remaining.
  "one-left-this-week": claimed(1, 6),
  // Week at zero, month still above zero. The month counter keeps its real
  // number; only its note changes.
  "weekly-limit-reached": claimed(0, 6),
  // Month at zero.
  "monthly-allowance-used-up": claimed(0, 0),
  "member-briefs-available": member(16),
  "member-out-of-allowance": member(0),
  // Nothing ordered yet: counters full, table replaced by the empty state.
  "brand-new-account": claimed(ENTITLEMENTS.claimed.weeklyCap, ENTITLEMENTS.claimed.monthlyAllowance),
  // Bought balance above zero. Both counters read zero and ordering still works.
  "bought-briefs-on-hand": claimed(0, 0, 3),
};

export const ALL_STATES = [...GATE_STATES, ...Object.keys(PRODUCT_STATES)];

export const DEFAULT_STATE = "briefs-available";

// The one rule that governs all of this: the card is blocked only when the plan
// is exhausted AND the bought balance is zero. Bought briefs are an override.
export const deriveAllowance = (state) => {
  const a = PRODUCT_STATES[state] || PRODUCT_STATES[DEFAULT_STATE];
  const isMember = a.tier === "member";
  const tier = isMember ? ENTITLEMENTS.member : ENTITLEMENTS.claimed;
  const hasBought = a.bought > 0;

  const weekAtCap = a.week != null && a.week.remaining === 0;
  const monthAtCap = a.month.remaining === 0;
  const planExhausted = weekAtCap || monthAtCap;
  const blocked = planExhausted && !hasBought;

  let banner = null;
  if (blocked && monthAtCap) {
    banner = { tone: "coral", showMembershipPitch: !isMember };
  } else if (blocked && weekAtCap) {
    banner = { tone: "amber", showMembershipPitch: !isMember };
  }

  return {
    ...a,
    isMember,
    tier,
    hasBought,
    blocked,
    banner,
    // The month note explains why a real number is unreachable today.
    monthHeldByWeek: blocked && weekAtCap && !monthAtCap,
    weekAtCap,
    // A Member already holds every benefit the pitch sells.
    showMembershipCta: !isMember,
    resetDate: RESET_DATES.resetDate,
    weekResetDate: RESET_DATES.weekResetDate,
  };
};
